import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isHttpClientError } from "../../../lib/http/errors";
import { createUuid } from "../../../lib/trace/context";
import { normalizeEditorEndDate, validateEditorDates } from "../service/itineraryEditorService";
import { normalizeRailwayOvernightStays } from "../service/itineraryRailwayOvernightService";
import { itineraryDateSaveErrorMessage } from "../service/itinerarySaveErrorService";
import * as itineraryRepo from "../repo/itineraryRepo";
import { getRailwayTransfers } from "../repo/itineraryRailwayTransferRepo";
import {
  applyDrivingMeasurement,
  applyTransportWorkspaceChange,
  captureTransportWorkspaceBaseline,
  applyWalkingMeasurement,
  collectAffectedSummarySlots,
  itineraryEditorSemanticFingerprint,
  hasPersistedTransportFacts,
  localTransportEndpoint,
  normalizeExplicitProfessionalRoutes,
  queryLocalTransport,
  validateEditor,
  validateTransportPlacement,
} from "../service";
import {
  rebuildAdjacentSegments,
  hasExplicitProfessionalRoutes,
  normalizeTemporaryEntityIds,
  repairInvalidTransportEndpointSegments,
  requiresRouteConfirmation,
  isPendingTransportSource,
  synchronizeDayStayAreas,
} from "../service/itineraryEditorService";
import { activeSummarySlots, allSummarySlots, effectiveSummarySource, effectiveSummaryText, markEditedDayContent, collectAffectedPointDescriptions, composeDaySummary, hasTextContent, isManualSummarySource, pendingContentSlots, pointDescriptionFingerprint, summaryFieldBySlot, summaryMaterialFingerprint, summarySourceFieldBySlot, type AutoSummarySlot, type PendingPointRewriteIds, type PendingSummaryRewriteSlots } from "../service/itinerarySummaryService";
import { itineraryRequestFailureMessage } from "../service/itineraryErrorService";
export { itineraryRequestFailureMessage };
import { queryAutomaticTimetable } from "../service/itineraryTimetableService";
import { applyRailwayTransferAutomaticMeasurement, applyRailwayTransferAutomaticTimetable, isRailwayTransferTimetablePending, railwayTransferAutomaticBaseline, railwayTransferDepartureDate } from "../service/itineraryRailwayTransferService";
import type {
  ItineraryEditor,
  ItineraryEditorDocument,
  ItineraryMoveResult,
  ItinerarySegment,
  ItineraryValidationIssue,
  ItineraryValidationResult,
} from "../types";
import {
  browserItineraryLocalDraftStore,
  type ItineraryLocalDraft,
  type ItineraryLocalDraftStore,
} from "./itineraryLocalDraftStore";

export type ItinerarySaveStatus = "clean" | "dirty" | "local_saved" | "saving" | "saved" | "error";

type EditorRepo = Pick<
  typeof itineraryRepo,
  "getEditor" | "saveEditor" | "validateEditorDraft"
> & Partial<Pick<
  typeof itineraryRepo,
  | "parseDayWithAi"
  | "enrichItinerary"
  | "completePointDescriptions"
  | "createMainData"
  | "listPointCandidates"
  | "listMajorCandidates"
  | "getRailwayOptions"
  | "getFlightOptions"
  | "getDrivingRoute"
  | "getWalkingRoute"
>> & { getRailwayTransfers?: typeof getRailwayTransfers };

const defaultEditorRepo: EditorRepo = { ...itineraryRepo, getRailwayTransfers };

type TimerDriver = {
  setTimeout: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void;
};

export type ItineraryEditorRuntimeOptions = {
  draftDemandId?: string;
  automaticTransportEnabled?: boolean;
  repo?: EditorRepo;
  debounceMs?: number;
  timer?: TimerDriver;
  localDraftStore?: ItineraryLocalDraftStore;
  now?: () => string;
};

type PendingSave = { generation: number; version: number; payload: ItineraryEditor };
type InFlightSave = { generation: number; promise: Promise<boolean> };
type AutomaticMeasurement = { controller: AbortController; baseline: string; promise: Promise<void> };
type PointRequestWaiter = {
  resolve: (release: () => void) => void;
  reject: (reason: unknown) => void;
  signal?: AbortSignal;
  onAbort?: () => void;
};
type PointRequestSemaphore = { active: number; waiting: PointRequestWaiter[] };
type LoadedEditorSources = {
  server: ItineraryEditorDocument;
  local: ItineraryLocalDraft | null;
};
type EntityIdMaps = {
  day: ReadonlyMap<string, string>;
  node: ReadonlyMap<string, string>;
  segment: ReadonlyMap<string, string>;
  track: ReadonlyMap<string, string>;
};

const defaultTimer: TimerDriver = {
  setTimeout: (callback, delay) => globalThis.setTimeout(callback, delay),
  clearTimeout: (timer) => globalThis.clearTimeout(timer),
};

/** 稳定引用避免普通渲染重新启动生成调度。 */
const defaultNow = () => new Date().toISOString();

const MAX_POINT_DESCRIPTION_CONCURRENCY = 4;

/** 释放一个点位请求名额，并按 FIFO 顺序唤醒仍有效的等待者。 */
function drainPointRequestQueue(queue: PointRequestSemaphore): void {
  while (queue.active < MAX_POINT_DESCRIPTION_CONCURRENCY && queue.waiting.length) {
    const waiter = queue.waiting.shift()!;
    if (waiter.signal && waiter.onAbort) waiter.signal.removeEventListener("abort", waiter.onAbort);
    if (waiter.signal?.aborted) {
      waiter.reject(waiter.signal.reason ?? new DOMException("请求已取消", "AbortError"));
      continue;
    }
    queue.active += 1;
    let released = false;
    waiter.resolve(() => {
      if (released) return;
      released = true;
      queue.active -= 1;
      drainPointRequestQueue(queue);
    });
  }
}

/** 为本编辑会话的所有点位入口提供有界并发，并允许取消尚未发出的请求。 */
function acquirePointRequestSlot(queue: PointRequestSemaphore, signal?: AbortSignal): Promise<() => void> {
  return new Promise((resolve, reject) => {
    const waiter: PointRequestWaiter = { resolve, reject, signal };
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException("请求已取消", "AbortError"));
    } else {
      waiter.onAbort = () => {
        const index = queue.waiting.indexOf(waiter);
        if (index >= 0) queue.waiting.splice(index, 1);
        reject(signal?.reason ?? new DOMException("请求已取消", "AbortError"));
      };
      signal?.addEventListener("abort", waiter.onAbort, { once: true });
      queue.waiting.push(waiter);
      drainPointRequestQueue(queue);
    }
  });
}

/** 只比较目标路线、端点位置与专业归属，避免并行测算的邻段结果互相作废。 */
function automaticMeasurementBaseline(editor: ItineraryEditor, segment: ItinerarySegment): string {
  return JSON.stringify({
    segment,
    endpoints: editor.days.flatMap((day) => day.nodes.flatMap((node, index) => (
      node.id === segment.fromNodeId || node.id === segment.toNodeId
        ? [{
            node, index, dayId: day.id, dayDate: day.dayDate,
            tracks: day.parallelTracks?.tracks.flatMap((track) => track.nodeIds.includes(node.id)
              ? [{ id: track.id, majorId: track.majorId, nodeIndex: track.nodeIds.indexOf(node.id) }]
              : []),
          }]
        : []
    ))),
  });
}

/** 服务端成功确认访问和资源存在后，才允许恢复浏览器草稿。 */
async function loadEditorSources(
  itineraryId: string,
  repo: EditorRepo,
  localDraftStore: ItineraryLocalDraftStore,
): Promise<LoadedEditorSources> {
  const [serverResult, localResult] = await Promise.allSettled([
    repo.getEditor(itineraryId),
    localDraftStore.load(itineraryId),
  ]);
  if (serverResult.status === "rejected") throw serverResult.reason;
  const local = localResult.status === "fulfilled" ? localResult.value : null;
  return {
    server: serverResult.value,
    local,
  };
}

/** 只恢复基于当前服务端版本的人工草稿，客户端时钟不参与新旧裁决。 */
function shouldUseLocalDraft(
  local: ItineraryLocalDraft | null,
  server: ItineraryEditorDocument,
): local is ItineraryLocalDraft {
  if (!local || local.baseRevision !== server.revision) return false;
  if (
    itineraryEditorSemanticFingerprint(local.payload)
    === itineraryEditorSemanticFingerprint(server.payload)
  ) return false;
  return true;
}

/** 将前序快照已确定的临时标识映射传播到后续完整快照。 */
function remapEditorEntityIds(editor: ItineraryEditor, maps: EntityIdMaps): ItineraryEditor {
  const replace = (ids: ReadonlyMap<string, string>, id: string) => ids.get(id) ?? id;
  return {
    ...editor,
    days: editor.days.map((day) => ({
      ...day,
      id: replace(maps.day, day.id),
      nodes: day.nodes.map((node) => ({ ...node, id: replace(maps.node, node.id) })),
      parallelTracks: day.parallelTracks
        ? {
            ...day.parallelTracks,
            tracks: day.parallelTracks.tracks.map((track) => ({
              ...track,
              id: replace(maps.track, track.id),
              nodeIds: track.nodeIds.map((id) => replace(maps.node, id)),
              segmentIds: track.segmentIds.map((id) => replace(maps.segment, id)),
            })),
          }
        : day.parallelTracks,
    })),
    segments: editor.segments.map((segment) => ({
      ...segment,
      id: replace(maps.segment, segment.id),
      startDayId: replace(maps.day, segment.startDayId),
      endDayId: replace(maps.day, segment.endDayId),
      fromNodeId: replace(maps.node, segment.fromNodeId),
      toNodeId: replace(maps.node, segment.toNodeId),
    })),
  };
}

/** 从归一化前后的同构快照提取本轮新增标识映射。 */
function entityIdMaps(before: ItineraryEditor, after: ItineraryEditor): EntityIdMaps {
  const pairs = (source: string[], target: string[]) => new Map(source.flatMap((id, index) => {
    const replacement = target[index];
    return replacement && id !== replacement ? [[id, replacement] as const] : [];
  }));
  return {
    day: pairs(before.days.map((day) => day.id), after.days.map((day) => day.id)),
    node: pairs(
      before.days.flatMap((day) => day.nodes.map((node) => node.id)),
      after.days.flatMap((day) => day.nodes.map((node) => node.id)),
    ),
    segment: pairs(before.segments.map((segment) => segment.id), after.segments.map((segment) => segment.id)),
    track: pairs(
      before.days.flatMap((day) => day.parallelTracks?.tracks.map((track) => track.id) ?? []),
      after.days.flatMap((day) => day.parallelTracks?.tracks.map((track) => track.id) ?? []),
    ),
  };
}

/** 补齐可确定的相邻线路，已有显式专业关系由共用重建边界原样保留。 */
function hydrateEditorDocument(document: ItineraryEditorDocument): {
  document: ItineraryEditorDocument;
  addedSegmentIds: Set<string>;
} {
  const existingSegmentIds = new Set(document.payload.segments.map((segment) => segment.id));
  const payload = normalizeEditorEndDate(normalizeExplicitProfessionalRoutes(document.payload, createUuid));
  const segments = rebuildAdjacentSegments(
    payload.days,
    payload.segments,
    createUuid,
  );
  return {
    document: {
      ...document,
      payload: { ...payload, segments },
    },
    addedSegmentIds: new Set(
      segments.filter((segment) => !existingSegmentIds.has(segment.id)).map((segment) => segment.id),
    ),
  };
}

/** 返回降级读取后仍需人工确认的路线问题。 */
function manualRepairIssues(
  editor: ItineraryEditor,
): ItineraryValidationIssue[] {
  const nodeNames = new Map(
    editor.days.flatMap((day) => day.nodes.map((node) => [node.id, node.title] as const)),
  );
  return editor.segments
    .filter((segment) => requiresRouteConfirmation(segment))
    .map((segment) => ({
      code: "manual-repair-segment-unconfirmed",
      detail: `请人工核对并保存 ${nodeNames.get(segment.fromNodeId) ?? "起点"} → ${nodeNames.get(segment.toNodeId) ?? "终点"} 的交通路线`,
      subjectKey: segment.id,
    }));
}

/** 提示专业交通读取缺口，不阻断当前编辑进度保存。 */
function incompleteProfessionalReadIssues(document: ItineraryEditorDocument | null): ItineraryValidationIssue[] {
  if (!document) return [];
  const { payload } = document;
  const hasUnresolvedRoutes = payload.days.some((day) => hasExplicitProfessionalRoutes(day, payload.segments));
  const hasProfessionalRoutes = payload.days.some((day) => day.parallelTracks?.tracks.length)
    || payload.segments.some((segment) => segment.majorId);
  // 可见路径恢复成功并不代表接口已返回全部交通，不能因此丢掉漏读保护。
  const hasSkippedRoutes = (document.previewSkippedSegmentCount ?? 0) > 0 && hasProfessionalRoutes;
  if (!hasUnresolvedRoutes && !hasSkippedRoutes) return [];
  return [{
    code: "professional-route-read-incomplete",
    detail: document.previewSkippedSegmentCount
      ? `当前行程有 ${document.previewSkippedSegmentCount} 条交通未完整读取，可先保存当前编辑进度，交通信息待后续完善。`
      : "专业路线尚未形成完整的分流与汇合路径，可先保存当前编辑进度。",
  }];
}

/** 仅追踪可由人工保存解除的待确认路线。 */
function editableRepairSegmentIds(
  editor: ItineraryEditor,
  addedSegmentIds: ReadonlySet<string>,
): Set<string> {
  return new Set(editor.segments
    .filter((segment) => addedSegmentIds.has(segment.id) && requiresRouteConfirmation(segment))
    .map((segment) => segment.id));
}

/** 行程编辑器运行时，负责本地草稿、显式后端保存与校验。 */
export function useItineraryEditorRuntime(
  itineraryId: string,
  options: ItineraryEditorRuntimeOptions = {},
) {
  const queryClient = useQueryClient();
  const repo = useMemo(() => options.repo ?? (options.draftDemandId
    ? { ...defaultEditorRepo, ...itineraryRepo.unsavedDemandRepo(options.draftDemandId) }
    : defaultEditorRepo), [options.repo, options.draftDemandId]);
  const debounceMs = options.debounceMs ?? 800;
  const timer = options.timer ?? defaultTimer;
  const localDraftStore = options.localDraftStore ?? browserItineraryLocalDraftStore;
  const now = options.now ?? defaultNow;
  const automaticTransportEnabled = options.automaticTransportEnabled ?? false;
  const query = useQuery({
    queryKey: ["itinerary", "editor-runtime", itineraryId],
    queryFn: () => loadEditorSources(itineraryId, repo, localDraftStore),
    enabled: Boolean(itineraryId),
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const [document, setDocument] = useState<ItineraryEditorDocument | null>(null);
  const [editor, setEditor] = useState<ItineraryEditor | null>(null);
  const [selectedDayId, setSelectedDayId] = useState("");
  const [saveStatus, setSaveStatus] = useState<ItinerarySaveStatus>("clean");
  const [saveError, setSaveError] = useState<unknown>(null);
  const [reloading, setReloading] = useState(false);
  const [reloadError, setReloadError] = useState<unknown>(null);
  const reloadingRef = useRef(false);
  const reloadAttemptedRef = useRef(false);
  const [validationIssues, setValidationIssues] = useState<ItineraryValidationIssue[]>([]);
  const [repairRequiredCount, setRepairRequiredCount] = useState(0);
  const [repairRemainingCount, setRepairRemainingCount] = useState(0);
  const documentRef = useRef<ItineraryEditorDocument | null>(null);
  const editorRef = useRef<ItineraryEditor | null>(null);
  const versionRef = useRef(0);
  const localQueueRef = useRef<PendingSave[]>([]);
  const serverQueueRef = useRef<PendingSave[]>([]);
  const activePendingRef = useRef<PendingSave | null>(null);
  const inFlightRef = useRef<InFlightSave | null>(null);
  const localInFlightRef = useRef<InFlightSave | null>(null);
  const persistedLocalDraftRef = useRef<ItineraryLocalDraft | null>(null);
  const localDraftInvalidatedRef = useRef(false);
  const serverDirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const activeAiRequestCountRef = useRef(0);
  const [activeAiRequestCount, setActiveAiRequestCount] = useState(0);
  const needsNormalizationRef = useRef(false);
  const repairSegmentIdsRef = useRef<Set<string>>(new Set());
  const repairModeRef = useRef(false);
  const automaticMeasurementsRef = useRef(new Map<string, AutomaticMeasurement>());
  const pointRequestSemaphoreRef = useRef<PointRequestSemaphore>({ active: 0, waiting: [] });
  const pointRequestKeysRef = useRef(new Map<string, { signal?: AbortSignal }>());
  const measureLoadedSegmentsRef = useRef(false);
  const [calculatingSegmentIds, setCalculatingSegmentIds] = useState<ReadonlySet<string>>(new Set());
  const manualSegmentVersionsRef = useRef(new Map<string, number>());
  const pendingSummaryRewriteRef = useRef<PendingSummaryRewriteSlots>({});
  const summaryModeVersionsRef = useRef(new Map<string, number>());
  const forcedSummaryRewriteRef = useRef<PendingSummaryRewriteSlots>({});
  const pendingPointRewriteRef = useRef<PendingPointRewriteIds>({});
  const summaryRewriteInFlightRef = useRef<{ generation: number; promise: Promise<void>; controller: AbortController } | null>(null);
  const [summaryRewriting, setSummaryRewriting] = useState(false);
  const [summaryRewriteWarning, setSummaryRewriteWarning] = useState("");
  const summaryRewriteFailureRef = useRef("");
  const localDraftSaveFailureRef = useRef<unknown>(null);

  const updateVisibleDocument = useCallback((next: ItineraryEditorDocument) => {
    documentRef.current = next;
    setDocument(next);
  }, []);

  useEffect(() => {
    for (const measurement of automaticMeasurementsRef.current.values()) measurement.controller.abort();
    automaticMeasurementsRef.current.clear();
    measureLoadedSegmentsRef.current = false;
    setCalculatingSegmentIds(new Set());
    generationRef.current += 1;
    if (timerRef.current !== null) timer.clearTimeout(timerRef.current);
    timerRef.current = null;
    documentRef.current = null;
    editorRef.current = null;
    versionRef.current = 0;
    localQueueRef.current = [];
    serverQueueRef.current = [];
    activePendingRef.current = null;
    inFlightRef.current = null;
    localInFlightRef.current = null;
    persistedLocalDraftRef.current = null;
    localDraftInvalidatedRef.current = false;
    serverDirtyRef.current = false;
    activeAiRequestCountRef.current = 0;
    setActiveAiRequestCount(0);
    needsNormalizationRef.current = false;
    repairSegmentIdsRef.current = new Set();
    repairModeRef.current = false;
    pendingSummaryRewriteRef.current = {};
    summaryModeVersionsRef.current.clear();
    forcedSummaryRewriteRef.current = {};
    pendingPointRewriteRef.current = {};
    reloadingRef.current = false;
    reloadAttemptedRef.current = false;
    setReloading(false);
    setReloadError(null);
    setDocument(null);
    setEditor(null);
    setSelectedDayId("");
    setSaveStatus("clean");
    setSaveError(null);
    setValidationIssues([]);
    setRepairRequiredCount(0);
    setRepairRemainingCount(0);
    summaryRewriteInFlightRef.current?.controller.abort();
    summaryRewriteInFlightRef.current = null;
    setSummaryRewriting(false);
    summaryRewriteFailureRef.current = "";
    setSummaryRewriteWarning("");
  }, [itineraryId, timer]);

  const applyLoadedSources = useCallback((sources: LoadedEditorSources) => {
    const local = sources.local?.itineraryId === itineraryId ? sources.local : null;
    const localBaseRevision = local?.baseRevision;
    const useLocal = shouldUseLocalDraft(local, sources.server);
    const baseline = sources.server;
    if (!baseline || baseline.payload.id !== itineraryId) return false;
    const hydratedBaseline = hydrateEditorDocument(baseline);
    const hydratedVisible = useLocal && local
      ? hydrateEditorDocument({
          ...hydratedBaseline.document,
          payload: {
            ...local.payload,
            projectId: baseline.payload.projectId,
            sourceDemandId: baseline.payload.sourceDemandId,
            status: baseline.payload.status,
            maintenanceMode: baseline.payload.maintenanceMode,
            isConfirmed: baseline.payload.isConfirmed,
            isReviewing: baseline.payload.isReviewing ?? false,
          },
        })
      : hydratedBaseline;
    const nextEditor = hydratedVisible.document.payload;
    pendingSummaryRewriteRef.current = useLocal && local && local.pendingSummaryRewrites === undefined
      ? collectAffectedSummarySlots(hydratedBaseline.document.payload, nextEditor)
      : {};
    pendingPointRewriteRef.current = useLocal && local
      ? collectAffectedPointDescriptions(hydratedBaseline.document.payload, nextEditor)
      : {};
    forcedSummaryRewriteRef.current = {};
    for (const [dayId, ids] of Object.entries(pendingPointRewriteRef.current)) {
      const day = nextEditor.days.find((item) => item.id === dayId);
      forcedSummaryRewriteRef.current[dayId] = [...new Set(day?.nodes.filter((node) => ids.includes(node.id)).map((node) => node.timeSlot) ?? [])];
      if (day?.isFullDay || day?.fullDaySummary != null) forcedSummaryRewriteRef.current[dayId].push("full_day");
    }
    if (local && (useLocal || localBaseRevision === baseline.revision)) {
      for (const [dayId, slots] of Object.entries(local.pendingSummaryRewrites ?? {})) {
        if (!Array.isArray(slots) || !nextEditor.days.some((day) => day.id === dayId)) continue;
        pendingSummaryRewriteRef.current[dayId] = [...new Set([
          ...(pendingSummaryRewriteRef.current[dayId] ?? []),
          ...slots.filter((slot) => allSummarySlots.includes(slot)),
        ])];
      }
      for (const [dayId, nodeIds] of Object.entries(local.pendingPointRewrites ?? {})) {
        if (!Array.isArray(nodeIds) || !nextEditor.days.some((day) => day.id === dayId)) continue;
        pendingPointRewriteRef.current[dayId] = [...new Set([
          ...(pendingPointRewriteRef.current[dayId] ?? []), ...nodeIds.filter((id) => typeof id === "string"),
        ])];
      }
      for (const [dayId, slots] of Object.entries(local.forcedSummaryRewrites ?? {})) {
        if (!Array.isArray(slots) || !nextEditor.days.some((day) => day.id === dayId)) continue;
        forcedSummaryRewriteRef.current[dayId] = [...new Set([
          ...(forcedSummaryRewriteRef.current[dayId] ?? []),
          ...slots.filter((slot) => allSummarySlots.includes(slot)),
        ])];
        const day = nextEditor.days.find((item) => item.id === dayId);
        if (day?.isFullDay && day.fullDaySummary == null && slots.some((slot) => slot !== "full_day")) {
          forcedSummaryRewriteRef.current[dayId] = [...new Set<AutoSummarySlot>([...forcedSummaryRewriteRef.current[dayId], "full_day"])];
        }
      }
    }
    summaryRewriteInFlightRef.current?.controller.abort();
    summaryRewriteInFlightRef.current = null;
    setSummaryRewriting(false);
    summaryRewriteFailureRef.current = "";
    setSummaryRewriteWarning("");
    const repairSegmentIds = baseline.previewDegraded
      ? editableRepairSegmentIds(nextEditor, hydratedVisible.addedSegmentIds)
      : new Set<string>();
    const repairIssues = manualRepairIssues(nextEditor);
    const repairActive = Boolean(baseline.previewDegraded && repairIssues.length);
    repairModeRef.current = repairActive;
    repairSegmentIdsRef.current = repairSegmentIds;
    setRepairRequiredCount(repairActive
      ? baseline.previewSkippedSegmentCount ?? repairSegmentIds.size
      : 0);
    setRepairRemainingCount(repairIssues.length);
    needsNormalizationRef.current = Boolean(baseline.requiresPersistedNormalization)
      || JSON.stringify(hydratedBaseline.document.payload) !== JSON.stringify(baseline.payload);
    versionRef.current += 1;
    localQueueRef.current = [];
    serverQueueRef.current = [];
    serverDirtyRef.current = useLocal;
    persistedLocalDraftRef.current = useLocal ? local : null;
    localDraftInvalidatedRef.current = false;
    updateVisibleDocument(hydratedBaseline.document);
    editorRef.current = nextEditor;
    measureLoadedSegmentsRef.current = true;
    setEditor(nextEditor);
    setSelectedDayId(nextEditor.days[0]?.id ?? "");
    setValidationIssues([...validateEditor(nextEditor), ...repairIssues, ...incompleteProfessionalReadIssues(hydratedBaseline.document)]);
    setSaveError(null);
    setSaveStatus(useLocal ? "local_saved" : "clean");
    if (local && !useLocal && !Object.keys(pendingSummaryRewriteRef.current).length) void localDraftStore.remove(itineraryId, local);
    return true;
  }, [itineraryId, localDraftStore, updateVisibleDocument]);

  useEffect(() => {
    if (reloadAttemptedRef.current || !query.isSuccess || query.isFetching || !query.data || documentRef.current) return;
    applyLoadedSources(query.data);
  }, [applyLoadedSources, query.data, query.isFetching, query.isSuccess]);

  useEffect(() => {
    mountedRef.current = true;
    const measurements = automaticMeasurementsRef.current;
    return () => {
      mountedRef.current = false;
      summaryRewriteInFlightRef.current?.controller.abort();
      for (const measurement of measurements.values()) measurement.controller.abort();
      measurements.clear();
      if (timerRef.current !== null) timer.clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [timer]);

  useEffect(() => localDraftStore.subscribe?.(itineraryId, (draft) => {
    const current = editorRef.current;
    const persisted = persistedLocalDraftRef.current;
    if (!serverDirtyRef.current || !current || reloadingRef.current) return;
    if (draft && itineraryEditorSemanticFingerprint(draft.payload) === itineraryEditorSemanticFingerprint(current)) {
      persistedLocalDraftRef.current = draft;
      if (localDraftInvalidatedRef.current) {
        setSaveError(null);
        setSaveStatus((status) => status === "saving" ? status : "local_saved");
      }
      localDraftInvalidatedRef.current = false;
      return;
    }
    if (draft && persisted
      && itineraryEditorSemanticFingerprint(draft.payload) === itineraryEditorSemanticFingerprint(persisted.payload)
      && JSON.stringify(draft.pendingSummaryRewrites ?? {}) === JSON.stringify(persisted.pendingSummaryRewrites ?? {})) return;
    localDraftInvalidatedRef.current = true;
    setSaveError(new Error("本地草稿已被其他页面更新或清除，当前修改仅保留在本页面。请保留此页面并点击保存，将当前内容保存到服务器。"));
    setSaveStatus((status) => status === "saving" ? status : "error");
    // 外部变化只撤销安全承诺；不得自动重写缓存与其他标签争抢。
    // 首次采用服务端基线后重新核对，覆盖本地读取与采用之间的外部改动。
  }), [document, itineraryId, localDraftStore]);

  const flushLocalQueue = useCallback((allowInvalidatedBackup = false): Promise<boolean> => {
    const generation = generationRef.current;
    if (localDraftInvalidatedRef.current && !allowInvalidatedBackup) return Promise.resolve(false);
    if (localInFlightRef.current?.generation === generation) return localInFlightRef.current.promise;
    const run = async () => {
      while (
        generation === generationRef.current
        && localQueueRef.current[0]?.generation === generation
      ) {
        const pending = localQueueRef.current[0];
        const currentDocument = documentRef.current;
        if (!pending || !currentDocument) return false;
        try {
          const draft: ItineraryLocalDraft = {
            schemaVersion: 1,
            itineraryId,
            baseRevision: currentDocument.revision,
            updatedAt: now(),
            payload: pending.payload,
            // 空队列也是事实：刷新后不能仅因初始化交通回填与服务端有差异而重建文案任务。
            pendingSummaryRewrites: structuredClone(pendingSummaryRewriteRef.current),
            ...(Object.keys(pendingPointRewriteRef.current).length
              ? { pendingPointRewrites: structuredClone(pendingPointRewriteRef.current) } : {}),
            ...(Object.keys(forcedSummaryRewriteRef.current).length
              ? { forcedSummaryRewrites: structuredClone(forcedSummaryRewriteRef.current) } : {}),
          };
          await localDraftStore.save(draft);
          if (generation === generationRef.current) {
            localDraftSaveFailureRef.current = null;
            persistedLocalDraftRef.current = draft;
            localDraftInvalidatedRef.current = false;
          }
        } catch (error) {
          if (generation !== generationRef.current) return false;
          localDraftSaveFailureRef.current = error;
          if (mountedRef.current) {
            setSaveError(error);
            setSaveStatus("error");
          }
          return false;
        }
        if (generation !== generationRef.current) return false;
        localQueueRef.current.shift();
        if (pending.version === versionRef.current && mountedRef.current) {
          setSaveStatus("local_saved");
          setSaveError(null);
        }
      }
      return generation === generationRef.current && localQueueRef.current.length === 0;
    };
    const promise = run().finally(() => {
      if (localInFlightRef.current?.generation === generation) localInFlightRef.current = null;
    });
    localInFlightRef.current = { generation, promise };
    return promise;
  }, [itineraryId, localDraftStore, now]);

  const drainServerQueue = useCallback((): Promise<boolean> => {
    const generation = generationRef.current;
    if (inFlightRef.current?.generation === generation) return inFlightRef.current.promise;
    const run = async () => {
      while (
        generation === generationRef.current
        && serverQueueRef.current[0]?.generation === generation
      ) {
        const pending = serverQueueRef.current[0];
        if (!pending) break;
        const currentDocument = documentRef.current;
        if (!currentDocument) return false;
        const resourceIssues = validateEditor(pending.payload, {
          requireNodeResources: true,
          allowedSnapshotNodeIds: new Set(currentDocument.payload.days.flatMap((day) => day.nodes)
            .filter((node) => node.nodeType === "custom").map((node) => node.id)),
        }).filter((issue) => issue.code === "node-resource-required");
        if (resourceIssues.length) {
          if (mountedRef.current) {
            setValidationIssues(resourceIssues);
            setSaveStatus("error");
            setSaveError(new Error(resourceIssues[0].detail));
          }
          return false;
        }
        if (mountedRef.current) {
          setSaveStatus("saving");
          setSaveError(null);
        }
        serverQueueRef.current.shift();
        activePendingRef.current = pending;
        try {
          const normalizedIds = normalizeTemporaryEntityIds(normalizeExplicitProfessionalRoutes(pending.payload, createUuid), createUuid);
          const idMaps = entityIdMaps(pending.payload, normalizedIds.editor);
          pendingSummaryRewriteRef.current = Object.fromEntries(Object.entries(pendingSummaryRewriteRef.current)
            .map(([dayId, slots]) => [idMaps.day.get(dayId) ?? dayId, slots]));
          forcedSummaryRewriteRef.current = Object.fromEntries(Object.entries(forcedSummaryRewriteRef.current)
            .map(([dayId, slots]) => [idMaps.day.get(dayId) ?? dayId, slots]));
          pendingPointRewriteRef.current = Object.fromEntries(Object.entries(pendingPointRewriteRef.current)
            .map(([dayId, ids]) => [idMaps.day.get(dayId) ?? dayId, ids.map((id) => idMaps.node.get(id) ?? id)]));
          if ([idMaps.day, idMaps.node, idMaps.segment, idMaps.track].some((ids) => ids.size)) {
            serverQueueRef.current = serverQueueRef.current.map((queued) => ({
              ...queued,
              payload: remapEditorEntityIds(queued.payload, idMaps),
            }));
            localQueueRef.current = localQueueRef.current.map((queued) => ({
              ...queued,
              payload: remapEditorEntityIds(queued.payload, idMaps),
            }));
            if (editorRef.current) {
              const remappedEditor = remapEditorEntityIds(editorRef.current, idMaps);
              editorRef.current = remappedEditor;
              if (mountedRef.current) setEditor(remappedEditor);
            }
            if (mountedRef.current) {
              setSelectedDayId((id) => idMaps.day.get(id) ?? id);
            }
          }
          const normalizedPayload = {
            ...normalizedIds.editor,
            segments: rebuildAdjacentSegments(
              normalizedIds.editor.days,
              normalizedIds.editor.segments,
              createUuid,
            ),
          };
          pending.payload = normalizedPayload;
          repairSegmentIdsRef.current = new Set(
            [...repairSegmentIdsRef.current].map((id) => normalizedIds.segmentIdMap.get(id) ?? id),
          );
          if (pending.version === versionRef.current) {
            editorRef.current = normalizedPayload;
            if (mountedRef.current) {
              setEditor(normalizedPayload);
              setSelectedDayId((id) => normalizedIds.dayIdMap.get(id) ?? id);
            }
          }
          await repo.saveEditor(itineraryId, {
            revision: currentDocument.revision,
            payload: normalizedPayload,
          });
          // 保存后回读原子写入的正文、兼容模式与版本，作为下一轮编辑的服务器基线。
          const saved = await repo.getEditor(itineraryId).catch((cause: unknown) => {
            throw new Error("行程已经保存成功，但页面没能重新取得保存后的内容。你的修改还在当前页面，请点“重试保存”；如果仍然失败，请把错误详情发给管理员，先不要关闭页面。", { cause });
          });
          if (generation !== generationRef.current) return false;
          if (activePendingRef.current === pending) activePendingRef.current = null;
          needsNormalizationRef.current = false;
          repairSegmentIdsRef.current = new Set();
          repairModeRef.current = false;
          setRepairRequiredCount(saved.previewSkippedSegmentCount ?? 0);
          const pendingIssues = manualRepairIssues(saved.payload);
          setRepairRemainingCount(pendingIssues.length);
          setValidationIssues([
            ...validateEditor(saved.payload), ...pendingIssues, ...incompleteProfessionalReadIssues(saved),
          ]);
          updateVisibleDocument(saved);
          // 后端已确认的快照无需再次本地落盘；仅保留提交期间产生的新编辑。
          localQueueRef.current = localQueueRef.current.filter((queued) => queued.version > pending.version);
          serverDirtyRef.current = pending.version !== versionRef.current
            || localQueueRef.current.length > 0;
          if (pending.version === versionRef.current && serverQueueRef.current.length === 0) {
            // 保存事务补齐素材也会使另一模式的旧稿过期；失效队列必须随保存快照持久化。
            const affected = collectAffectedSummarySlots(normalizedPayload, saved.payload);
            for (const [dayId, slots] of Object.entries(affected)) {
              pendingSummaryRewriteRef.current[dayId] = [...new Set([
                ...(pendingSummaryRewriteRef.current[dayId] ?? []), ...slots,
              ])];
            }
            // 后端本次补齐的字段解除待生成标记；未回填的素材变更仍留给内容编辑入口。
            for (const day of saved.payload.days) {
              const submitted = normalizedPayload.days.find((item) => item.id === day.id);
              const completed = activeSummarySlots(day).filter((slot) =>
                !hasTextContent(submitted?.[summaryFieldBySlot[slot]]) && hasTextContent(day[summaryFieldBySlot[slot]]));
              for (const ref of [pendingSummaryRewriteRef, forcedSummaryRewriteRef]) {
                const remaining = ref.current[day.id]?.filter((slot) => !completed.includes(slot));
                if (remaining?.length) ref.current[day.id] = remaining;
                else delete ref.current[day.id];
              }
              const remainingPoints = pendingPointRewriteRef.current[day.id]?.filter((id) => {
                const before = submitted?.nodes.find((node) => node.id === id);
                const after = day.nodes.find((node) => node.id === id);
                return hasTextContent(before?.description) || !hasTextContent(after?.description);
              });
              if (remainingPoints?.length) pendingPointRewriteRef.current[day.id] = remainingPoints;
              else delete pendingPointRewriteRef.current[day.id];
            }
            if (!Object.keys(pendingContentSlots(saved.payload, pendingSummaryRewriteRef.current, pendingPointRewriteRef.current)).length) {
              setSummaryRewriteWarning("");
            }
            editorRef.current = saved.payload;
            if (mountedRef.current) {
              setEditor(saved.payload);
              setSaveStatus(serverDirtyRef.current
                ? localDraftInvalidatedRef.current ? "error" : "local_saved"
                : "saved");
            }
          }
          if (!serverDirtyRef.current) {
            localDraftInvalidatedRef.current = false;
            localDraftSaveFailureRef.current = null;
            setSaveError(null);
            if (Object.keys(pendingSummaryRewriteRef.current).length) {
              const draft: ItineraryLocalDraft = { schemaVersion: 1, itineraryId, baseRevision: saved.revision,
                updatedAt: new Date().toISOString(), payload: saved.payload,
                pendingSummaryRewrites: structuredClone(pendingSummaryRewriteRef.current),
                pendingPointRewrites: structuredClone(pendingPointRewriteRef.current),
                forcedSummaryRewrites: structuredClone(forcedSummaryRewriteRef.current) };
              try {
                await localDraftStore.save(draft);
                if (generation === generationRef.current) persistedLocalDraftRef.current = draft;
              } catch {
                // 完整内容已由后端确认，辅助任务备份失败不能把服务器保存改判为失败。
              }
            } else void localDraftStore.remove(itineraryId, persistedLocalDraftRef.current);
          }
          void queryClient.invalidateQueries({ queryKey: ["itinerary", "list"] });
        } catch (error) {
          if (generation !== generationRef.current) return false;
          let visibleError = error;
          if (isHttpClientError(error) && error.code === "ITINERARY_REFERENCE_INVALID") {
            let repairedAny = false;
            const repairedQueue = [pending, ...serverQueueRef.current].map((queued) => {
              const repaired = repairInvalidTransportEndpointSegments(
                queued.payload,
                createUuid,
                currentDocument.payload,
              );
              repairedAny ||= JSON.stringify(repaired.segments) !== JSON.stringify(queued.payload.segments);
              return { ...queued, payload: repaired };
            });
            if (repairedAny) {
              serverQueueRef.current = repairedQueue;
              const latest = repairedQueue.at(-1);
              if (latest?.version === versionRef.current) editorRef.current = latest.payload;
              visibleError = new Error(
                "历史火车或航班的两端不是对应站点，已转为普通步行；请在预览中核对，必要时修改实际交通后重试保存。",
              );
              if (mountedRef.current) {
                if (latest?.version === versionRef.current) setEditor(latest.payload);
                setValidationIssues(validateEditor(latest?.payload ?? pending.payload));
              }
            } else {
              serverQueueRef.current.unshift(pending);
            }
          } else {
            serverQueueRef.current.unshift(pending);
          }
          if (activePendingRef.current === pending) activePendingRef.current = null;
          if (mountedRef.current) {
            setSaveError(visibleError);
            setSaveStatus("error");
          }
          return false;
        }
      }
      return generation === generationRef.current && serverQueueRef.current.length === 0;
    };
    const promise = run().finally(() => {
      if (inFlightRef.current?.generation === generation) inFlightRef.current = null;
    });
    inFlightRef.current = { generation, promise };
    return promise;
  }, [itineraryId, localDraftStore, queryClient, repo, updateVisibleDocument]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current !== null) timer.clearTimeout(timerRef.current);
    const generation = generationRef.current;
    timerRef.current = timer.setTimeout(() => {
      timerRef.current = null;
      if (generation === generationRef.current) void flushLocalQueue();
    }, debounceMs);
  }, [debounceMs, flushLocalQueue, timer]);

  const updateEditor = useCallback(
    function updateEditorDraft(
      update: ItineraryEditor | ((current: ItineraryEditor) => ItineraryEditor),
      automatic = false,
      initializeMeasurements = false,
      preserveDayContentState = false,
    ) {
      const current = editorRef.current;
      if (!current || reloadingRef.current) return;
      const synchronized = initializeMeasurements ? current
        : synchronizeDayStayAreas(current, normalizeRailwayOvernightStays(typeof update === "function" ? update(current) : update));
      // 初始化测算只补交通草稿，不代表人工进入了内容修改；真实骨架编辑仍走正常标记和重写。
      const next = initializeMeasurements || preserveDayContentState
        ? synchronized : markEditedDayContent(current, synchronized);
      for (const day of next.days) {
        if (current.days.find((item) => item.id === day.id)?.isFullDay !== day.isFullDay) {
          summaryModeVersionsRef.current.set(day.id, (summaryModeVersionsRef.current.get(day.id) ?? 0) + 1);
          summaryRewriteInFlightRef.current?.controller.abort();
          summaryRewriteInFlightRef.current = null;
          setSummaryRewriting(false);
        }
      }
      if (!initializeMeasurements && !preserveDayContentState) {
        const affected = collectAffectedSummarySlots(current, next);
        const affectedPoints = collectAffectedPointDescriptions(current, next);
        if (Object.keys(affected).length) {
          const merged = { ...pendingSummaryRewriteRef.current };
          for (const [dayId, slots] of Object.entries(affected)) {
            merged[dayId] = [...new Set([...(merged[dayId] ?? []), ...slots])];
          }
          pendingSummaryRewriteRef.current = merged;
        }
        if (Object.keys(affectedPoints).length) {
          const merged = { ...pendingPointRewriteRef.current };
          const forced = { ...forcedSummaryRewriteRef.current };
          for (const [dayId, ids] of Object.entries(affectedPoints)) {
            merged[dayId] = [...new Set([...(merged[dayId] ?? []), ...ids])];
            const day = next.days.find((item) => item.id === dayId);
            const slots: AutoSummarySlot[] = [...new Set(ids.flatMap((id) => {
              const node = day?.nodes.find((item) => item.id === id);
              return node ? [node.timeSlot] : [];
            }))];
            if (day?.isFullDay || day?.fullDaySummary != null) slots.push("full_day");
            if (slots.length) forced[dayId] = [...new Set([...(forced[dayId] ?? []), ...slots])];
          }
          pendingPointRewriteRef.current = merged;
          forcedSummaryRewriteRef.current = forced;
        }
        // 专业变化请求尚未返回时，计调直接改写点位介绍视为新的人工素材，旧 AI 结果失效。
        // 对应时段仍待重写，并会读取这份最新人工素材。
        if (!automatic) {
          const pending = { ...pendingPointRewriteRef.current };
          for (const [dayId, ids] of Object.entries(pending)) {
            const before = current.days.find((day) => day.id === dayId);
            const after = next.days.find((day) => day.id === dayId);
            const remaining = ids.filter((id) => {
              const oldNode = before?.nodes.find((node) => node.id === id);
              const newNode = after?.nodes.find((node) => node.id === id);
              return Boolean(oldNode && newNode && oldNode.description === newNode.description);
            });
            if (remaining.length) pending[dayId] = remaining;
            else delete pending[dayId];
          }
          pendingPointRewriteRef.current = pending;
        }
      }
      // 显式单项重写也使用 updateEditor，成功回填后解除该时段旧的待更新提示。
      if (!initializeMeasurements) {
        for (const day of next.days) {
          const before = current.days.find((item) => item.id === day.id);
          for (const slot of allSummarySlots) {
            const source = day[summarySourceFieldBySlot[slot]];
            if (!before
              || (!isManualSummarySource(source) && !hasTextContent(day[summaryFieldBySlot[slot]]))
              || (!isManualSummarySource(source) && before[summaryFieldBySlot[slot]] === day[summaryFieldBySlot[slot]]
                && before[summarySourceFieldBySlot[slot]] === source)) continue;
            const remaining = pendingSummaryRewriteRef.current[day.id]?.filter((item) => item !== slot);
            if (remaining?.length) pendingSummaryRewriteRef.current[day.id] = remaining;
            else delete pendingSummaryRewriteRef.current[day.id];
            if (!automatic) {
              const forced = forcedSummaryRewriteRef.current[day.id]?.filter((item) => item !== slot);
              if (forced?.length) forcedSummaryRewriteRef.current[day.id] = forced;
              else delete forcedSummaryRewriteRef.current[day.id];
            }
          }
        }
      }
      if (!automatic && !initializeMeasurements) {
        const currentSegments = new Map(current.segments.map((item) => [item.id, JSON.stringify(item)]));
        for (const item of next.segments) {
          if (currentSegments.get(item.id) !== JSON.stringify(item)) {
            manualSegmentVersionsRef.current.set(item.id, (manualSegmentVersionsRef.current.get(item.id) ?? 0) + 1);
          }
        }
        for (const item of current.segments) {
          if (!next.segments.some((candidate) => candidate.id === item.id)) {
            manualSegmentVersionsRef.current.set(item.id, (manualSegmentVersionsRef.current.get(item.id) ?? 0) + 1);
          }
        }
      }
      if (!Object.keys(pendingContentSlots(next, pendingSummaryRewriteRef.current, pendingPointRewriteRef.current)).length) {
        summaryRewriteFailureRef.current = "";
        setSummaryRewriteWarning("");
      }
      const generation = generationRef.current;
      if (!initializeMeasurements) {
        const version = versionRef.current + 1;
        versionRef.current = version;
        editorRef.current = next;
        localQueueRef.current.push({ generation, version, payload: structuredClone(next) });
        serverDirtyRef.current = true;
        setEditor(next);
        const repairIssues = manualRepairIssues(next);
        setRepairRemainingCount(repairIssues.length);
        setValidationIssues([...validateEditor(next), ...repairIssues, ...incompleteProfessionalReadIssues(documentRef.current)]);
        setSaveStatus(localDraftInvalidatedRef.current ? "error" : "dirty");
        if (!localDraftInvalidatedRef.current) setSaveError(null);
        // 待确认交通仍属于可恢复的编辑进度，点位修改和其他编辑必须先写入本地草稿。
        // 人工确认只影响服务端保存校验，不应阻断前端缓存，否则刷新会丢失刚编辑的考察点。
        scheduleSave();
      }

      const measurements = automaticMeasurementsRef.current;
      const nextSegments = new Map(next.segments.map((segment) => [segment.id, segment]));
      const cancelledMeasurementIds = new Set<string>();
      for (const [id, measurement] of measurements) {
        const segment = nextSegments.get(id);
        if (!segment || automaticMeasurementBaseline(next, segment) !== measurement.baseline) {
          measurement.controller.abort();
          measurements.delete(id);
          if (segment && (isPendingTransportSource(segment.sourceType)
            || (requiresRouteConfirmation(segment) && !hasPersistedTransportFacts(segment)))) cancelledMeasurementIds.add(id);
        }
      }
      const previousIds = new Set(current.segments.map((segment) => segment.id));
      const nodes = new Map(next.days.flatMap((day) => day.nodes.map((node) => [node.id, node] as const)));
      for (const segment of next.segments) {
        const previousSegment = current.segments.find((item) => item.id === segment.id);
        const endpointsChanged = Boolean(previousSegment) && [previousSegment!.fromNodeId, previousSegment!.toNodeId]
          .some((nodeId) => {
            const previousNode = current.days.flatMap((day) => day.nodes).find((node) => node.id === nodeId);
            const nextNode = nodes.get(nodeId);
            return previousNode && nextNode && JSON.stringify([
              previousNode.nodeType, previousNode.pointSpuId, previousNode.stayAreaId,
              previousNode.organizationSiteId, previousNode.title,
            ]) !== JSON.stringify([
              nextNode.nodeType, nextNode.pointSpuId, nextNode.stayAreaId,
              nextNode.organizationSiteId, nextNode.title,
            ]);
          });
        const endpointTimeSlotConstraint = {
          departureSlot: segment.automaticQueryDepartureSlot ?? (isRailwayTransferTimetablePending(segment)
            ? nodes.get(segment.fromNodeId)?.timeSlot : undefined),
        };
        // 中转以 manual 固定异站大巴方式；没有事实的待确认段仍需即时测算。
        const pendingSource = isPendingTransportSource(segment.sourceType);
        const emptyUnconfirmed = requiresRouteConfirmation(segment) && !hasPersistedTransportFacts(segment);
        // 自动回填已经完成的目标段不能因实际车站归一再查询自身。
        if (automatic && !pendingSource && !emptyUnconfirmed) continue;
        const resetForEndpointEdit = Boolean(previousSegment && (
          endpointsChanged
          || (pendingSource && !isPendingTransportSource(previousSegment.sourceType))
          || previousSegment.transportMode !== segment.transportMode
          || previousSegment.routeType !== segment.routeType
        ));
        const wasCancelledMeasurement = cancelledMeasurementIds.has(segment.id);
        if (initializeMeasurements
          ? !emptyUnconfirmed
          : (previousIds.has(segment.id) && !resetForEndpointEdit && !wasCancelledMeasurement)
            || (!wasCancelledMeasurement && !pendingSource && !emptyUnconfirmed)) continue;
        if (measurements.has(segment.id)) continue;
        const isTimetable = segment.transportMode === "rail" || segment.transportMode === "air";
        if (!isTimetable && ((segment.transportMode !== "bus" && segment.transportMode !== "walk") || !repo.getDrivingRoute || !repo.getWalkingRoute)) continue;
        const from = nodes.get(segment.fromNodeId);
        const to = nodes.get(segment.toNodeId);
        if (!from || !to) continue;
        const controller = new AbortController();
        const baseline = automaticMeasurementBaseline(next, segment);
        const transferBaseline = isTimetable ? railwayTransferAutomaticBaseline(next, segment.id) : null;
        const insertionBaseline = isTimetable && segment.sourcePayload?.railwayInsertionPlacement
          ? captureTransportWorkspaceBaseline(next, segment) : null;
        const incidentVersions = new Map(next.segments.filter((item) => item.id !== segment.id
          && [segment.fromNodeId, segment.toNodeId].some((id) => id === item.fromNodeId || id === item.toNodeId))
          .map((item) => [item.id, manualSegmentVersionsRef.current.get(item.id) ?? 0]));
        const request = { origin: localTransportEndpoint(from), destination: localTransportEndpoint(to) };
        const query = isTimetable
          ? queryAutomaticTimetable(segment, from, to, railwayTransferDepartureDate(segment)
              ?? next.days.find((day) => day.id === segment.startDayId)?.dayDate, repo, controller.signal, now, endpointTimeSlotConstraint)
          : queryLocalTransport(request, { getDrivingRoute: repo.getDrivingRoute!, getWalkingRoute: repo.getWalkingRoute! }, controller.signal, segment.transportMode === "walk" ? "walk" : "bus")
            .then((measurement) => {
              const draft = { ...segment, transportMode: measurement.mode, routeType: measurement.mode === "walk" ? "local_transfer" as const : "charter_bus" as const };
              const queryKey = `${request.origin.name}-${request.destination.name}`;
              return { endpointChanges: [], segment: measurement.mode === "walk"
                ? applyWalkingMeasurement(draft, measurement.result, now(), queryKey)
                : applyDrivingMeasurement(draft, measurement.result, now(), queryKey) };
            });
        const promise = query.then((measured) => {
            const latest = editorRef.current;
            const target = latest?.segments.find((item) => item.id === segment.id);
            if (controller.signal.aborted || !mountedRef.current || generation !== generationRef.current
              || !measured || !latest || !target || automaticMeasurementBaseline(latest, target) !== baseline) return;
            if (transferBaseline !== null && railwayTransferAutomaticBaseline(latest, segment.id) !== transferBaseline) return;
            const railwayTransfer = "railwayTransfer" in measured ? measured.railwayTransfer : undefined;
            if (measured.endpointChanges.length || measured.segment.transportMode === "rail") {
              const incidentIds = new Set([...incidentVersions.keys(), ...latest.segments
                .filter((item) => item.id !== segment.id
                  && [segment.fromNodeId, segment.toNodeId].some((id) => id === item.fromNodeId || id === item.toNodeId))
                .map((item) => item.id)]);
              if ([...incidentIds].some((id) => (manualSegmentVersionsRef.current.get(id) ?? 0)
                !== (incidentVersions.get(id) ?? 0))) return;
            }
            if (railwayTransfer && insertionBaseline) {
              updateEditorDraft(applyTransportWorkspaceChange(latest, {
                ...insertionBaseline,
                // 人工版本已核对；只吸收并行自动测算，端点、顺序与专业基线仍使用请求前快照。
                previousIncidentSegments: insertionBaseline.previousIncidentSegments.map((previous) =>
                  latest.segments.find((item) => item.id === previous.id) ?? previous),
                segment: measured.segment, endpointChanges: measured.endpointChanges, railwayTransfer,
              }), true, false, initializeMeasurements || preserveDayContentState);
              return;
            }
            updateEditorDraft(measured.endpointChanges.length || measured.segment.transportMode === "rail"
              ? applyRailwayTransferAutomaticTimetable(latest, applyTransportWorkspaceChange(latest, {
                  ...captureTransportWorkspaceBaseline(latest, target),
                  segment: measured.segment, endpointChanges: measured.endpointChanges,
                }), segment.id)
              : applyRailwayTransferAutomaticMeasurement(latest, measured.segment), true, false, initializeMeasurements || preserveDayContentState);
          })
          .catch(() => { /* 查询或测算失败时保留待确认路线，允许用户在工作区重试。 */ })
          .finally(() => {
            if (measurements.get(segment.id)?.controller === controller) {
              measurements.delete(segment.id);
              if (mountedRef.current) setCalculatingSegmentIds(new Set(measurements.keys()));
            }
          });
        measurements.set(segment.id, { controller, baseline, promise });
      }
      setCalculatingSegmentIds(new Set(measurements.keys()));
    },
    [now, repo, scheduleSave],
  );

  useEffect(() => {
    if (!editor || reloading || !automaticTransportEnabled || !measureLoadedSegmentsRef.current) return;
    measureLoadedSegmentsRef.current = false;
    if (editor.isConfirmed || !["draft", "active"].includes(editor.status)) return;
    // 完成服务端/本地草稿选择后再调度；启动查询本身不制造待保存改动。
    updateEditor((current) => current, true, true);
  }, [automaticTransportEnabled, editor, reloading, updateEditor]);

  const requestPointDescriptionForNode = useCallback(async (
    request: Parameters<typeof itineraryRepo.completePointDescriptions>[1],
    dayId: string,
    nodeId: string,
    baseline: string,
    generation: number,
    signal?: AbortSignal,
  ) => {
    if (!repo.completePointDescriptions) throw new Error("点位介绍补全暂不可用");
    const key = JSON.stringify([generation, dayId, nodeId]);
    const previous = pointRequestKeysRef.current.get(key);
    if (previous && !previous.signal?.aborted) return null;
    const entry = { signal };
    pointRequestKeysRef.current.set(key, entry);
    let release: (() => void) | undefined;
    try {
      release = await acquirePointRequestSlot(pointRequestSemaphoreRef.current, signal);
      if (signal?.aborted) throw signal.reason ?? new DOMException("请求已取消", "AbortError");
      if (!mountedRef.current || generation !== generationRef.current) return null;
      const latest = editorRef.current;
      if (!latest || pointDescriptionFingerprint(latest, dayId, nodeId) !== baseline) return null;
      const response = await repo.completePointDescriptions(itineraryId, {
        ...request, dayId, nodeIds: [nodeId], editor: latest,
      }, signal);
      const current = editorRef.current;
      return !signal?.aborted && mountedRef.current && generation === generationRef.current && current
        && pointDescriptionFingerprint(current, dayId, nodeId) === baseline ? response : null;
    } finally {
      release?.();
      if (pointRequestKeysRef.current.get(key) === entry) pointRequestKeysRef.current.delete(key);
    }
  }, [itineraryId, repo]);

  // 保存只刷新已失效的 AI 正文；空白补缺仍由后端完成，人工稿不参与强制重写。
  const pendingSaveSummaryRewrites = useCallback(() => Object.entries(pendingSummaryRewriteRef.current)
    .flatMap(([dayId, slots]) => {
      const day = editorRef.current?.days.find((item) => item.id === dayId);
      const selected = day ? activeSummarySlots(day).filter((slot) => (slots.includes(slot)
          || (slot === "full_day" && day.fullDaySummary == null && slots.some((item) => item !== "full_day")))
        && !isManualSummarySource(effectiveSummarySource(day, slot))
        && hasTextContent(effectiveSummaryText(day, slot))) : [];
      return selected.length ? [{ dayId, slots: selected }] : [];
    }), []);

  const rewritePendingSummarySlots = useCallback(async (mode: "content" | "save" = "content"): Promise<void> => {
    if (!repo.enrichItinerary || reloadingRef.current || activeAiRequestCountRef.current
      || inFlightRef.current || !editorRef.current || editorRef.current.isConfirmed
      || !["draft", "active", "reviewing"].includes(editorRef.current.status)) return;
    const generation = generationRef.current;
    if (summaryRewriteInFlightRef.current?.generation === generation) return summaryRewriteInFlightRef.current.promise;
    const controller = new AbortController();
    const modeVersions = new Map(summaryModeVersionsRef.current);
    const isSameMode = (dayId: string) => !controller.signal.aborted
      && (modeVersions.get(dayId) ?? 0) === (summaryModeVersionsRef.current.get(dayId) ?? 0);
    const run = async () => {
      while (automaticMeasurementsRef.current.size) {
        await Promise.allSettled([...automaticMeasurementsRef.current.values()].map((item) => item.promise));
        if (!mountedRef.current || generation !== generationRef.current || controller.signal.aborted) return;
      }
      const refreshPendingContent = () => {
        if (!editorRef.current || controller.signal.aborted) return;
        const tasks = mode === "save" ? pendingSaveSummaryRewrites().map(({ dayId, slots }) => [dayId, slots] as const)
          : Object.entries(pendingContentSlots(
          editorRef.current, pendingSummaryRewriteRef.current, pendingPointRewriteRef.current,
        ));
        for (const [dayId, slots] of tasks) pendingSummaryRewriteRef.current[dayId] = [...new Set([...(pendingSummaryRewriteRef.current[dayId] ?? []), ...slots])];
      };
      refreshPendingContent();
      const collectTasks = () => mode === "save" ? pendingSaveSummaryRewrites() : Object.entries(pendingContentSlots(
        editorRef.current!, pendingSummaryRewriteRef.current, pendingPointRewriteRef.current,
      ))
        .flatMap(([dayId, slots]) => {
          const day = editorRef.current?.days.find((item) => item.id === dayId);
          const selected = day && isSameMode(dayId) ? activeSummarySlots(day).filter((slot) => slots.includes(slot)) : [];
          return selected.length ? [{ dayId, slots: selected }] : [];
        });
      const tasks = collectTasks();
      summaryRewriteFailureRef.current = "";
      const failureReasons = new Set<string>();
      if (!tasks.length) {
        setSummaryRewriteWarning("");
        return;
      }
      setSummaryRewriting(true);
      setSummaryRewriteWarning("");
      const queueBaseline = JSON.stringify([pendingSummaryRewriteRef.current, pendingPointRewriteRef.current, forcedSummaryRewriteRef.current]);
      const removePending = (dayId: string, slot: AutoSummarySlot) => {
        const next = { ...pendingSummaryRewriteRef.current };
        next[dayId] = (next[dayId] ?? []).filter((item) => item !== slot);
        if (!next[dayId].length) delete next[dayId];
        pendingSummaryRewriteRef.current = next;
      };
      const removeForced = (dayId: string, slot: AutoSummarySlot) => {
        const next = { ...forcedSummaryRewriteRef.current };
        next[dayId] = (next[dayId] ?? []).filter((item) => item !== slot);
        if (!next[dayId].length) delete next[dayId];
        forcedSummaryRewriteRef.current = next;
      };
      const removePoint = (dayId: string, nodeId: string) => {
        const next = { ...pendingPointRewriteRef.current };
        next[dayId] = (next[dayId] ?? []).filter((item) => item !== nodeId);
        if (!next[dayId].length) delete next[dayId];
        pendingPointRewriteRef.current = next;
      };
      // 先补齐整轮学习材料，避免后续介绍回填使已完成的相邻时段再次过期。
      const failedMaterials = new Set<string>();
      const pointTasks = (editorRef.current?.days ?? []).flatMap((day) => {
        const slots = tasks.find((task) => task.dayId === day.id)?.slots ?? [];
        const ids = new Set([...(pendingPointRewriteRef.current[day.id] ?? []).filter((id) =>
          day.nodes.some((node) => node.id === id && (slots.includes("full_day") || slots.includes(node.timeSlot)))),
          ...day.nodes.filter((node) => (slots.includes("full_day") || slots.includes(node.timeSlot)) && node.nodeType === "point"
            && !hasTextContent(node.description)).map((node) => node.id)]);
        return [...ids].map((nodeId) => ({ dayId: day.id, nodeId }));
      });
      await Promise.allSettled(pointTasks.map(async ({ dayId, nodeId }) => {
        if (!mountedRef.current || generation !== generationRef.current || !editorRef.current || !isSameMode(dayId)) return;
        const before = editorRef.current;
        const day = before.days.find((item) => item.id === dayId);
        const node = day?.nodes.find((item) => item.id === nodeId);
        if (!day || !node || node.nodeType !== "point" || !node.pointSpuId?.trim()) {
          removePoint(dayId, nodeId);
          if (node?.nodeType === "point" && !hasTextContent(node.description)) failedMaterials.add(`${dayId}:${node.timeSlot}`);
          return;
        }
        const baseline = pointDescriptionFingerprint(before, dayId, nodeId);
        try {
          const response = await requestPointDescriptionForNode({ overwrite: true }, dayId, nodeId, baseline, generation, controller.signal);
          if (!mountedRef.current || generation !== generationRef.current || !isSameMode(dayId)) return;
          const latest = editorRef.current;
          const current = latest?.days.find((item) => item.id === dayId)?.nodes.find((item) => item.id === nodeId);
          if (!response || !latest || !current || pointDescriptionFingerprint(latest, dayId, nodeId) !== baseline) {
            // 节点、介绍或实际专业在排队/请求期间变化：丢弃该点旧响应，保留待处理状态。
            failedMaterials.add(`${dayId}:${node.timeSlot}`);
            return;
          }
          const description = response.items.find((item) => item.nodeId === nodeId && item.status === "generated")?.description;
          if (!hasTextContent(description)) throw new Error("点位介绍没有返回有效内容");
          updateEditor({ ...latest, days: latest.days.map((item) => item.id === dayId ? {
            ...item, nodes: item.nodes.map((candidate) => candidate.id === nodeId
              ? { ...candidate, description } : candidate),
          } : item) }, true);
          removePoint(dayId, nodeId);
        } catch (error) {
          if (!mountedRef.current || generation !== generationRef.current || controller.signal.aborted) return;
          failureReasons.add(itineraryRequestFailureMessage(error));
          failedMaterials.add(`${dayId}:${node.timeSlot}`);
        }
      }));
      refreshPendingContent();
      // 点位回填也可能使原本已有正文的相邻时段失效，纳入同一轮处理。
      tasks.splice(0, tasks.length, ...collectTasks());
      let taskIndex = 0;
      const worker = async () => {
        while (taskIndex < tasks.length) {
          const { dayId, slots } = tasks[taskIndex++];
          // 同日串行，下午读取上午回填后的最新草稿；不同日期仍可并发。
          for (const slot of slots) {
            if (!mountedRef.current || generation !== generationRef.current || controller.signal.aborted) return;
            const current = editorRef.current;
            const day = current?.days.find((item) => item.id === dayId);
            if (!current || !day) {
              removePending(dayId, slot);
              continue;
            }
            if (!isSameMode(dayId) || !activeSummarySlots(day).includes(slot)) continue;
            if (isManualSummarySource(effectiveSummarySource(day, slot))
              || (slot === "full_day" ? [...failedMaterials].some((key) => key.startsWith(`${dayId}:`)) : failedMaterials.has(`${dayId}:${slot}`))
              || day.nodes.some((node) => (slot === "full_day" || node.timeSlot === slot) && pendingPointRewriteRef.current[dayId]?.includes(node.id))) continue;
            try {
              const outcome = await composeDaySummary({ dayId, slot, automatic: true, descriptionsPrepared: true,
                signal: controller.signal,
                getEditor: () => editorRef.current!,
                onChange: (next) => updateEditor(next, true),
                enrich: (request, signal) => repo.enrichItinerary!(itineraryId, request, signal),
                isCurrent: () => mountedRef.current && generation === generationRef.current && isSameMode(dayId) && Boolean(editorRef.current),
              });
              if (!mountedRef.current || generation !== generationRef.current || controller.signal.aborted) return;
              if (outcome === "updated" || outcome === "empty") {
                removePending(dayId, slot);
                removeForced(dayId, slot);
              }
            } catch (error) {
              if (!mountedRef.current || generation !== generationRef.current || controller.signal.aborted) return;
              failureReasons.add(itineraryRequestFailureMessage(error));
              // AI 失败保留原文和待重写标记，完成重试前禁止数据库保存。
            }
          }
        }
      };
      await Promise.allSettled(Array.from({ length: Math.min(4, tasks.length) }, worker));
      if (!mountedRef.current || generation !== generationRef.current || controller.signal.aborted) return;
      const remaining = pendingContentSlots(editorRef.current!, pendingSummaryRewriteRef.current, pendingPointRewriteRef.current);
      const pending = Object.entries(remaining).flatMap(([dayId, slots]) => {
        const day = editorRef.current?.days.find((item) => item.id === dayId);
        return day ? slots.filter((slot) => activeSummarySlots(day).includes(slot))
          .map((slot) => `第${day.dayNumber}天${slot === "full_day" ? "全天" : slot === "morning" ? "上午" : slot === "afternoon" ? "下午" : "晚上"}`) : [];
      });
      const pendingPoints = Object.entries(pendingPointRewriteRef.current).flatMap(([dayId, nodeIds]) => {
        const day = editorRef.current?.days.find((item) => item.id === dayId);
        return day ? nodeIds.map((nodeId) => {
          const node = day.nodes.find((item) => item.id === nodeId);
          return node && (remaining[dayId]?.includes("full_day") || remaining[dayId]?.includes(node.timeSlot))
            ? `第${day.dayNumber}天「${node.title}」点位介绍` : null;
        }).filter((item): item is string => item !== null) : [];
      });
      const pendingContent = [...new Set([...pendingPoints, ...pending])];
      summaryRewriteFailureRef.current = pendingContent.length ? [...failureReasons].join(" ") : "";
      if (pendingContent.length) setSummaryRewriteWarning(`这些内容还没生成成功：${pendingContent.join("、")}。原文已保留，可以重试，也可以手动填写后保存。人工编辑的内容会直接保留，不再自动生成。${summaryRewriteFailureRef.current ? ` ${summaryRewriteFailureRef.current}` : ""}`);
      else setSummaryRewriteWarning("");
      if (editorRef.current && queueBaseline !== JSON.stringify([
        pendingSummaryRewriteRef.current, pendingPointRewriteRef.current, forcedSummaryRewriteRef.current,
      ])) updateEditor(editorRef.current, true);
    };
    const promise = run().finally(() => {
      if (summaryRewriteInFlightRef.current?.controller === controller) {
        summaryRewriteInFlightRef.current = null;
        if (mountedRef.current) setSummaryRewriting(false);
      }
    });
    summaryRewriteInFlightRef.current = { generation, promise, controller };
    return promise;
  }, [itineraryId, pendingSaveSummaryRewrites, repo, requestPointDescriptionForNode, updateEditor]);

  // 缺失内容由显式保存的后端流程补齐，仅在前端生成尚未结束时避免竞态提交。
  const isContentSaveBlocked = useCallback(() => Boolean(
    activeAiRequestCountRef.current || summaryRewriteInFlightRef.current
  ), []);

  const persistToServer = useCallback(async (): Promise<boolean> => {
    if (reloadingRef.current || !editorRef.current || isContentSaveBlocked()) return false;
    const generation = generationRef.current;
    if (localDraftInvalidatedRef.current && localQueueRef.current.length === 0) {
      localQueueRef.current.push({ generation, version: versionRef.current, payload: structuredClone(editorRef.current) });
    }
    // 普通保存和重试均检查当前完整草稿；本地备份不能被当作服务器保存成功。
    const hasSaveIssue = () => {
      const current = editorRef.current;
      if (!current) return false;
      const normalized = normalizeEditorEndDate(current);
      // 派生日期不属于正文编辑，不触发人工接管或额外文案重写。
      if (normalized !== current) updateEditor(normalized, true, false, true);
      return validateEditorDates(normalized).length > 0 || validateTransportPlacement(normalized).length > 0;
    };
    const preserveValidationFailure = async (): Promise<boolean> => {
      if (timerRef.current !== null) timer.clearTimeout(timerRef.current);
      timerRef.current = null;
      const localSaved = await flushLocalQueue(true);
      if (!mountedRef.current || generation !== generationRef.current || !editorRef.current) return false;
      const dateIssue = validateEditorDates(editorRef.current)[0];
      const transportIssues = validateTransportPlacement(editorRef.current);
      if (!dateIssue && !transportIssues.length) return localSaved;
      const message = dateIssue
        ? itineraryDateSaveErrorMessage(dateIssue.code, editorRef.current)
        : `本次尚未保存：${transportIssues.map((issue) => issue.detail).join("\n")}`;
      setSaveStatus("error");
      setSaveError(new Error(localSaved ? message : `${message}本地草稿备份失败，当前修改仅保留在当前页面，请勿关闭。`,
        { cause: localSaved ? undefined : localDraftSaveFailureRef.current }));
      return false;
    };
    if (hasSaveIssue() && !await preserveValidationFailure()) return false;
    do {
      while (automaticMeasurementsRef.current.size) {
        await Promise.allSettled([
          ...automaticMeasurementsRef.current.values(),
        ].map((measurement) => measurement.promise));
        if (!mountedRef.current || generation !== generationRef.current) return false;
      }
      if (timerRef.current !== null) timer.clearTimeout(timerRef.current);
      timerRef.current = null;
      if (isContentSaveBlocked()) return false;
      if (!mountedRef.current || generation !== generationRef.current) return false;
      if (hasSaveIssue() && !await preserveValidationFailure()) return false;
      if (pendingSaveSummaryRewrites().length) {
        await rewritePendingSummarySlots("save");
        if (!mountedRef.current || generation !== generationRef.current) return false;
        if (hasSaveIssue() && !await preserveValidationFailure()) return false;
        if (pendingSaveSummaryRewrites().length) {
          const localSaved = await flushLocalQueue(true);
          if (!mountedRef.current || generation !== generationRef.current) return false;
          if (!localSaved) {
            const cause = localDraftSaveFailureRef.current;
            const reason = cause instanceof Error ? cause.message : "本地存储不可用";
            setSaveStatus("error");
            setSaveError(new Error(`本地草稿保存失败：${reason}。本次尚未保存到服务器，当前修改仅保留在当前页面，请勿关闭。请恢复本地存储后重试保存。`, { cause }));
            return false;
          }
          setSaveStatus("error");
          setSaveError(new Error("还有 AI 文案没更新成功，这次没有保存，草稿已保留。请点击“重试保存”，或手动修改上面提示的内容后再保存；人工编辑的内容会直接保留。"));
          return false;
        }
      }
      // 本地备份尽力完成，人工保存的成功以服务器提交和完整回读为准。
      await flushLocalQueue(true);
      if (inFlightRef.current?.generation === generationRef.current) {
        if (!await inFlightRef.current.promise) return false;
      }
      if (!mountedRef.current || generation !== generationRef.current || reloadingRef.current || isContentSaveBlocked()) return false;
      // 本地落盘或前次提交的等待期间仍允许编辑，最终提交前重新核对最新材料。
      if (automaticMeasurementsRef.current.size) continue;
      if (hasSaveIssue() && !await preserveValidationFailure()) return false;
      if (pendingSaveSummaryRewrites().length) {
        setSaveStatus("error");
        setSaveError(new Error("保存时行程安排又有变化，这次没有保存，草稿已保留。请再点一次保存，更新相关 AI 文案。"));
        return false;
      }
      // AI 行程首次显式保存必须落库，永久转人工维护只能由服务端成功结果确认。
      if (
        (serverDirtyRef.current || needsNormalizationRef.current || serverQueueRef.current.length > 0
          || Object.keys(pendingContentSlots(editorRef.current, pendingSummaryRewriteRef.current, pendingPointRewriteRef.current)).length > 0
          || editorRef.current.days.some((day) => !day.legacyCompatibility && day.nodes.some((node) =>
            node.nodeType === "point" && node.pointSpuId?.trim() && !hasTextContent(node.description)))
          || documentRef.current?.payload.maintenanceMode === "ai"
          || documentRef.current?.persisted === false)
        && editorRef.current
        && documentRef.current
      ) {
        // 每次提交都取用户当前可见内容，失败的旧请求不能挡住修复后的完整稿。
        serverQueueRef.current = [{
          generation: generationRef.current,
          version: versionRef.current,
          payload: structuredClone(editorRef.current),
        }];
      }
      if (serverQueueRef.current.length === 0) return true;
      if (!await drainServerQueue()) return false;
      // PUT 或回读期间的新编辑仍属于这次人工保存，全部落库后才报告成功。
    } while (serverDirtyRef.current || automaticMeasurementsRef.current.size);
    return true;
  }, [drainServerQueue, flushLocalQueue, isContentSaveBlocked, pendingSaveSummaryRewrites, rewritePendingSummarySlots, timer, updateEditor]);

  const save = useCallback(async () => {
    return persistToServer();
  }, [persistToServer]);

  const ensureSaved = useCallback(async () => {
    if (reloadingRef.current || !editorRef.current) return false;
    if (documentRef.current?.persisted === false) return false;
    if (timerRef.current !== null) timer.clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!await flushLocalQueue()) return false;
    if (inFlightRef.current?.generation === generationRef.current) {
      if (!await inFlightRef.current.promise) return false;
    }
    return serverQueueRef.current.length === 0
      && activePendingRef.current === null
      && !serverDirtyRef.current;
  }, [flushLocalQueue, timer]);

  const retrySave = useCallback(async () => {
    setSaveStatus("dirty");
    setSaveError(null);
    return persistToServer();
  }, [persistToServer]);

  const reload = useCallback(async () => {
    if (reloadingRef.current) return false;
    reloadingRef.current = true;
    reloadAttemptedRef.current = true;
    setReloading(true);
    setReloadError(null);
    for (const measurement of automaticMeasurementsRef.current.values()) measurement.controller.abort();
    automaticMeasurementsRef.current.clear();
    measureLoadedSegmentsRef.current = false;
    setCalculatingSegmentIds(new Set());
    const localWrite = localInFlightRef.current?.promise;
    generationRef.current += 1;
    const generation = generationRef.current;
    activeAiRequestCountRef.current = 0;
    setActiveAiRequestCount(0);
    if (timerRef.current !== null) timer.clearTimeout(timerRef.current);
    timerRef.current = null;
    // 重新加载即丢弃：失败后也不恢复旧草稿或待保存队列。
    localQueueRef.current = [];
    serverQueueRef.current = [];
    activePendingRef.current = null;
    inFlightRef.current = null;
    localInFlightRef.current = null;
    serverDirtyRef.current = false;
    documentRef.current = null;
    editorRef.current = null;
    pendingSummaryRewriteRef.current = {};
    pendingPointRewriteRef.current = {};
    forcedSummaryRewriteRef.current = {};
    summaryRewriteInFlightRef.current?.controller.abort();
    summaryRewriteInFlightRef.current = null;
    setDocument(null);
    setEditor(null);
    setSummaryRewriting(false);
    setSummaryRewriteWarning("");
    setSaveStatus("clean");
    setSaveError(null);
    try {
      // 等待已开始的本地写入落定，防止清理后旧写入又恢复已丢弃的草稿。
      await localWrite;
      if (generation !== generationRef.current) return false;
      await localDraftStore.remove(itineraryId);
      if (generation !== generationRef.current) return false;
      const sources = { server: await repo.getEditor(itineraryId), local: null };
      if (generation !== generationRef.current) return false;
      return applyLoadedSources(sources);
    } catch (error) {
      if (generation !== generationRef.current) return false;
      if (mountedRef.current) {
        setReloadError(error);
      }
      return false;
    } finally {
      if (generation === generationRef.current) {
        reloadingRef.current = false;
        if (mountedRef.current) setReloading(false);
      }
    }
  }, [applyLoadedSources, itineraryId, localDraftStore, repo, timer]);

  const acceptReviewState = useCallback((latest: ItineraryEditorDocument) => {
    if (latest.payload.id !== itineraryId || !editorRef.current) return;
    // 加载补段尚未持久化时也要保留原标识，供在途交通查询继续回填。
    const next = { ...editorRef.current, isReviewing: latest.payload.isReviewing ?? false,
      status: latest.payload.status, maintenanceMode: latest.payload.maintenanceMode,
      isConfirmed: latest.payload.isConfirmed };
    updateVisibleDocument(latest);
    if (serverDirtyRef.current) {
      updateEditor(next);
    } else {
      editorRef.current = next;
      setEditor(next);
    }
  }, [itineraryId, updateEditor, updateVisibleDocument]);

  const refreshMovedAssociation = useCallback(async (moved: ItineraryMoveResult) => {
    const generation = generationRef.current;
    if (moved.itineraryId !== itineraryId) return;
    await inFlightRef.current?.promise;
    const latest = await repo.getEditor(itineraryId);
    if (generation !== generationRef.current || !editorRef.current) return;
    const next = { ...editorRef.current, projectId: latest.payload.projectId,
      sourceDemandId: latest.payload.sourceDemandId, status: latest.payload.status,
      maintenanceMode: latest.payload.maintenanceMode,
      isConfirmed: latest.payload.isConfirmed, isReviewing: latest.payload.isReviewing ?? false };
    updateVisibleDocument(latest);
    if (serverDirtyRef.current) {
      updateEditor(next);
    } else {
      editorRef.current = next;
      setEditor(next);
    }
  }, [itineraryId, repo, updateEditor, updateVisibleDocument]);

  const reloadLatest = useCallback(async () => {
    await reload();
  }, [reload]);

  const validateDraft = useCallback(async (): Promise<ItineraryValidationResult | null> => {
    const generation = generationRef.current;
    const current = editorRef.current;
    if (!current) return null;
    const result = await repo.validateEditorDraft(itineraryId, current);
    if (generation !== generationRef.current) return null;
    setValidationIssues(result.issues);
    return result;
  }, [itineraryId, repo]);

  const parseDay = useCallback(async (dayId: string, prompt: string, signal?: AbortSignal) => {
    const current = editorRef.current;
    const day = current?.days.find((item) => item.id === dayId);
    if (!current || !day || !repo.parseDayWithAi) throw new Error("智能排程暂不可用");
    return repo.parseDayWithAi(itineraryId, {
      dayId,
      prompt,
      currentDay: day,
      currentSegments: current.segments.filter(
        (segment) => segment.startDayId === dayId && segment.endDayId === dayId,
      ),
      context: {
        itineraryTitle: current.title,
        itinerarySummary: current.summary,
        customerServiceStandards: current.customerServiceStandards,
        customerNotices: current.customerNotices,
      },
    }, signal);
  }, [itineraryId, repo]);

  const enrich = useCallback(async (request: Parameters<typeof itineraryRepo.enrichItinerary>[1], signal?: AbortSignal) => {
    if (!repo.enrichItinerary) throw new Error("智能文案暂不可用");
    const generation = generationRef.current;
    const dayId = request.dayId;
    const modeVersion = dayId ? summaryModeVersionsRef.current.get(dayId) ?? 0 : 0;
    const slot = request.summarySlot && request.summarySlot !== "daytime" ? request.summarySlot : null;
    const baseline = dayId && slot ? summaryMaterialFingerprint(request.editor, dayId, slot) : null;
    const before = request.editor.days.find((day) => day.id === dayId);
    if (dayId && slot && editorRef.current) {
      pendingSummaryRewriteRef.current[dayId] = [...new Set([...(pendingSummaryRewriteRef.current[dayId] ?? []), slot])];
      forcedSummaryRewriteRef.current[dayId] = [...new Set([...(forcedSummaryRewriteRef.current[dayId] ?? []), slot])];
      updateEditor(editorRef.current, true);
    }
    activeAiRequestCountRef.current += 1;
    setActiveAiRequestCount(activeAiRequestCountRef.current);
    try {
      const result = await repo.enrichItinerary(itineraryId, request, signal);
      const current = editorRef.current;
      if (dayId && slot && modeVersion !== (summaryModeVersionsRef.current.get(dayId) ?? 0)) return { days: [] };
      const day = current?.days.find((item) => item.id === dayId);
      const suggestion = result.days.find((item) => item.dayId === dayId);
      if (dayId && slot && current && before && day && generation === generationRef.current
        && !signal?.aborted && summaryMaterialFingerprint(current, dayId, slot) === baseline
        && effectiveSummaryText(day, slot) === effectiveSummaryText(before, slot)
        && effectiveSummarySource(day, slot) === effectiveSummarySource(before, slot)
        && (hasTextContent(suggestion?.[summaryFieldBySlot[slot]])
          || (suggestion?.composedSlots?.includes(slot) && suggestion.emptySlots?.includes(slot)))) {
        for (const ref of [pendingSummaryRewriteRef, forcedSummaryRewriteRef]) {
          const remaining = ref.current[dayId]?.filter((item) => item !== slot);
          if (remaining?.length) ref.current[dayId] = remaining;
          else delete ref.current[dayId];
        }
        if (![pendingSummaryRewriteRef.current, pendingPointRewriteRef.current]
          .some((pending) => Object.values(pending).some((items) => items.length))) {
          summaryRewriteFailureRef.current = "";
          setSummaryRewriteWarning("");
        }
      }
      return result;
    } catch (error) {
      if (generation === generationRef.current) setSummaryRewriteWarning("文案重写失败，原文已保留；请重试文案生成或人工修改后保存。");
      throw error;
    } finally {
      if (generation === generationRef.current) {
        activeAiRequestCountRef.current = Math.max(0, activeAiRequestCountRef.current - 1);
        setActiveAiRequestCount(activeAiRequestCountRef.current);
      }
    }
  }, [itineraryId, repo, updateEditor]);

  const completePointDescriptions = useCallback(async (
    request: Parameters<typeof itineraryRepo.completePointDescriptions>[1],
    signal?: AbortSignal,
  ) => {
    const generation = generationRef.current;
    if (!repo.completePointDescriptions) throw new Error("点位介绍补全暂不可用");
    const current = editorRef.current;
    if (!current) throw new Error("行程尚未加载完成");
    const targets = current.days.filter((day) => !request.dayId || day.id === request.dayId)
      .flatMap((day) => day.nodes.filter((node) => node.nodeType === "point" && node.pointSpuId
        && (!request.nodeIds || request.nodeIds.includes(node.id))
        && (request.overwrite || !hasTextContent(node.description))).map((node) => ({
          dayId: day.id, node, baseline: pointDescriptionFingerprint(current, day.id, node.id),
        })));
    if (!targets.length) {
      return { status: "success" as const, requestedCount: 0, successCount: 0, failedCount: 0,
        skippedCount: 0, items: [] };
    }
    for (const { dayId, node } of targets) {
      pendingPointRewriteRef.current[dayId] = [...new Set([...(pendingPointRewriteRef.current[dayId] ?? []), node.id])];
    }
    if (targets.length) updateEditor(current, true);
    activeAiRequestCountRef.current += 1;
    setActiveAiRequestCount(activeAiRequestCountRef.current);
    try {
      const outcomes = await Promise.allSettled(targets.map(async (target) => {
        const response = await requestPointDescriptionForNode(
          request, target.dayId, target.node.id, target.baseline, generation, signal,
        );
        if (!response) return { nodeId: target.node.id, status: "skipped" as const, errorCode: null };
        const item = response.items.find((entry) => entry.nodeId === target.node.id);
        const latest = editorRef.current;
        if (!signal?.aborted && mountedRef.current && generation === generationRef.current && latest
          && pointDescriptionFingerprint(latest, target.dayId, target.node.id) === target.baseline
          && ((item?.status === "generated" && hasTextContent(item.description))
            || (item?.status === "skipped" && hasTextContent(target.node.description)))) {
          const remaining = pendingPointRewriteRef.current[target.dayId]?.filter((id) => id !== target.node.id);
          if (remaining?.length) pendingPointRewriteRef.current[target.dayId] = remaining;
          else delete pendingPointRewriteRef.current[target.dayId];
        }
        return item ?? { nodeId: target.node.id, status: "failed" as const, errorCode: "invalid_output" as const };
      }));
      if (signal?.aborted) throw signal.reason ?? new DOMException("请求已取消", "AbortError");
      // 单点仍抛原始业务错误，让界面保留超时、限流和权限等具体处理提示。
      if (outcomes.length === 1 && outcomes[0].status === "rejected") throw outcomes[0].reason;
      const items = outcomes.map((outcome, index) => outcome.status === "fulfilled"
        ? outcome.value
        : ({ nodeId: targets[index].node.id, status: "failed" as const, errorCode: "upstream" as const }));
      const successCount = items.filter((item) => item.status === "generated").length;
      const failedCount = items.filter((item) => item.status === "failed").length;
      const skippedCount = items.filter((item) => item.status === "skipped").length;
      if (failedCount && mountedRef.current && generation === generationRef.current) {
        setSummaryRewriteWarning("部分点位介绍生成失败，成功内容已保留；请重试文案生成或人工补充后保存。");
      }
      return { status: failedCount ? successCount ? "partial_success" as const : "failed" as const : "success" as const,
        requestedCount: targets.length, successCount, failedCount, skippedCount, items };
    } catch (error) {
      if (generation === generationRef.current && !signal?.aborted) {
        setSummaryRewriteWarning("点位介绍生成失败，原文已保留；请重试文案生成或人工补充后保存。");
      }
      throw error;
    } finally {
      if (generation === generationRef.current) {
        activeAiRequestCountRef.current = Math.max(0, activeAiRequestCountRef.current - 1);
        setActiveAiRequestCount(activeAiRequestCountRef.current);
        if (activeAiRequestCountRef.current === 0 && localQueueRef.current.length > 0 && !repairModeRef.current) scheduleSave();
      }
    }
  }, [itineraryId, repo, requestPointDescriptionForNode, scheduleSave, updateEditor]);

  const createMainData = useCallback(async (request: Parameters<typeof itineraryRepo.createMainData>[1]) => {
    if (!repo.createMainData) throw new Error("新增主数据服务暂不可用");
    return repo.createMainData(itineraryId, request);
  }, [itineraryId, repo]);

  const loadDraftCustomizationInfo = useCallback(
    () => itineraryRepo.getItineraryCustomizationInfo(itineraryId, options.draftDemandId),
    [itineraryId, options.draftDemandId],
  );

  const listPoints = useCallback(async (query: Parameters<typeof itineraryRepo.listPointCandidates>[0], signal?: AbortSignal) => {
    if (!repo.listPointCandidates) throw new Error("点位候选暂不可用");
    return repo.listPointCandidates(query, signal);
  }, [repo]);

  const listMajors = useCallback(async (query: Parameters<typeof itineraryRepo.listMajorCandidates>[0] = {}) => {
    if (!repo.listMajorCandidates) throw new Error("专业候选暂不可用");
    return repo.listMajorCandidates(query);
  }, [repo]);

  const railway = useCallback(async (
    query: Parameters<typeof itineraryRepo.getRailwayOptions>[0],
    signal?: AbortSignal,
  ) => {
    if (!repo.getRailwayOptions) throw new Error("铁路查询暂不可用");
    return repo.getRailwayOptions(query, signal);
  }, [repo]);

  const flights = useCallback(async (
    query: Parameters<typeof itineraryRepo.getFlightOptions>[0],
    signal?: AbortSignal,
  ) => {
    if (!repo.getFlightOptions) throw new Error("航班查询暂不可用");
    return repo.getFlightOptions(query, signal);
  }, [repo]);

  const driving = useCallback(async (
    request: Parameters<typeof itineraryRepo.getDrivingRoute>[0],
    signal?: AbortSignal,
  ) => {
    if (!repo.getDrivingRoute) throw new Error("大巴测算暂不可用");
    return repo.getDrivingRoute(request, signal);
  }, [repo]);

  const walking = useCallback(async (
    request: Parameters<typeof itineraryRepo.getWalkingRoute>[0],
    signal?: AbortSignal,
  ) => {
    if (!repo.getWalkingRoute) throw new Error("步行测算暂不可用");
    return repo.getWalkingRoute(request, signal);
  }, [repo]);

  useEffect(() => {
    if (!editor) return;
    if (!editor.days.some((day) => day.id === selectedDayId)) {
      setSelectedDayId(editor.days[0]?.id ?? "");
    }
  }, [editor, selectedDayId]);

  return {
    document,
    editor,
    selectedDayId,
    saveStatus,
    saveError,
    validationIssues,
    repairRequiredCount,
    repairRemainingCount,
    loading: reloading || (!reloadAttemptedRef.current && (query.isPending || (query.isFetching && !documentRef.current))),
    loadError: reloadError ?? (document ? null : query.error),
    reloading,
    updateEditor,
    selectDay: setSelectedDayId,
    save,
    ensureSaved,
    acceptReviewState,
    retrySave,
    reload,
    reloadLatest,
    refreshMovedAssociation,
    validateDraft,
    parseDay,
    enrich,
    completePointDescriptions,
    createMainData,
    loadDraftCustomizationInfo,
    listPointCandidates: listPoints,
    listMajorCandidates: listMajors,
    getRailwayOptions: railway,
    getFlightOptions: flights,
    getDrivingRoute: driving,
    getWalkingRoute: walking,
    rewritePendingSummarySlots,
    summaryRewriting: summaryRewriting || activeAiRequestCount > 0,
    summaryRewriteWarning,
    contentSaveBlocked: summaryRewriting || activeAiRequestCount > 0 || Boolean(editor
      && Object.keys(pendingContentSlots(editor, pendingSummaryRewriteRef.current, pendingPointRewriteRef.current)).length),
    calculatingSegmentIds,
  };
}
